import { forwardRef, InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toolbarControl } from '@/lib/buttonStyles';

interface ToolbarSearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Small ghost text shown right-aligned inside the bar when input is empty. */
  rightHint?: string;
}

export const ToolbarSearchInput = forwardRef<HTMLInputElement, ToolbarSearchInputProps>(
  ({ value, onValueChange, className, placeholder = 'Search...', rightHint, style, ...props }, ref) => {
		const rootRef = useRef<HTMLDivElement | null>(null);
		const [canShowHint, setCanShowHint] = useState(false);
		const showRightHint = Boolean(rightHint && !value && canShowHint);

		useEffect(() => {
			const root = rootRef.current;
			if (!root || !rightHint) {
				setCanShowHint(false);
				return;
			}

			const update = () => setCanShowHint(root.offsetWidth >= 360);
			update();

			const observer = new ResizeObserver(update);
			observer.observe(root);
			return () => observer.disconnect();
		}, [rightHint]);

    return (
      <div
				ref={rootRef}
        className={cn(
					'flex items-center h-10 rounded-full overflow-hidden',
          'bg-[hsl(var(--toolbar-control-bg,var(--surface-1)))]',
          'border border-hairline',
          'transition-colors duration-smooth',
          'hover:border-white/20',
          'focus-within:border-white/30',
          toolbarControl,
          className
        )}
      >
				<div className="flex min-w-0 flex-1 items-center h-full gap-3 pl-3">
					<Search className="text-muted-foreground w-4 h-4 shrink-0 pointer-events-none" />
					<input
						ref={ref}
						type="search"
						inputMode="search"
						enterKeyHint="search"
						autoComplete="off"
						autoCorrect="off"
						autoCapitalize="none"
						spellCheck={false}
						value={value}
						onChange={(e) => onValueChange(e.target.value)}
						placeholder={placeholder}
						style={style}
						className={cn(
							'w-full h-full min-w-0 text-base',
							'bg-transparent border-0 shadow-none',
							'appearance-none [-webkit-appearance:none]',
							'[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
							'placeholder:text-muted-foreground text-ellipsis',
							'focus:outline-none focus-visible:outline-none',
							'disabled:cursor-not-allowed disabled:opacity-50'
						)}
						{...props}
					/>
				</div>
        {value ? (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              onValueChange('');
              const input = (e.currentTarget.parentElement?.querySelector('input') as HTMLInputElement | null);
              input?.focus();
            }}
            aria-label="Clear search"
						className="mr-1 inline-flex items-center justify-center w-8 h-8 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
				) : showRightHint ? (
          <span
						className="mr-3 ml-3 shrink-0 whitespace-nowrap border-l border-white/10 pl-3 text-xs text-muted-foreground/70 pointer-events-none"
            aria-hidden="true"
          >
            {rightHint}
          </span>
        ) : null}

      </div>
    );
  }
);

ToolbarSearchInput.displayName = 'ToolbarSearchInput';
